import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IconPhoto, IconDownload } from '@tabler/icons-react';
import { Media } from '../media/Media.tsx';
import { Spinner } from '../ui/Spinner.tsx';
import type { EventSummary } from '../../lib/types.ts';
import { coverKeyFor } from '../../hooks/useEvents.ts';
import { useDownload } from '../../hooks/useDownload.ts';
import { DownloadProgress } from './DownloadProgress.tsx';

/** Parse a YYYY-MM-DD string as a local date (avoids UTC off-by-one). */
function parseDay(day?: string): Date | null {
  if (!day) return null;
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** "12–14 Oct 2025", "19 Aug 2025", or "Aug 2025 – Oct 2025" across months/years. */
function formatDateRange(from?: string, to?: string): string | null {
  const a = parseDay(from);
  const b = parseDay(to) ?? a;
  if (!a) return null;
  const end = b ?? a;
  const day = (d: Date) => d.getDate();
  const mon = (d: Date) => d.toLocaleString('en-US', { month: 'short' });
  const yr = (d: Date) => d.getFullYear();

  if (a.getTime() === end.getTime()) return `${day(a)} ${mon(a)} ${yr(a)}`;
  if (yr(a) === yr(end) && a.getMonth() === end.getMonth())
    return `${day(a)}–${day(end)} ${mon(a)} ${yr(a)}`;
  if (yr(a) === yr(end)) return `${day(a)} ${mon(a)} – ${day(end)} ${mon(end)} ${yr(a)}`;
  return `${mon(a)} ${yr(a)} – ${mon(end)} ${yr(end)}`;
}

export function EventCard({ event, index }: { event: EventSummary; index: number }) {
  const cover = coverKeyFor(event);
  const dateRange = formatDateRange(event.dateFrom, event.dateTo);
  const download = useDownload();

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.3 }}
      className="group relative overflow-hidden rounded-lg border border-line bg-surface shadow-sm transition duration-200 ease-standard hover:scale-[1.02] hover:shadow-lg"
    >
      {/* Download-album button — a sibling of the Link (not nested, which would
          be invalid HTML). Always visible on mobile (no hover state); on
          desktop it reveals on hover / keyboard focus. 44px hit area for iOS. */}
      <button
        onClick={() => download.downloadArchive({ event: event.name })}
        disabled={download.busy}
        aria-label={`Download all photos in ${event.name}`}
        title="Download album"
        className="absolute right-1.5 top-1.5 z-10 grid h-11 w-11 place-items-center rounded-full bg-black/50 text-white backdrop-blur transition duration-200 ease-standard hover:bg-black/70 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-white disabled:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
      >
        {download.busy ? <Spinner className="h-4 w-4" /> : <IconDownload className="h-4 w-4" />}
      </button>

      <Link
        to={`/events/${encodeURIComponent(event.name)}`}
        className="block"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-grid">
          {cover ? (
            <Media
              fileKey={cover}
              kind="image"
              alt={event.name}
              className="h-full w-full transition duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="grid h-full w-full place-items-center text-muted">
              <IconPhoto className="h-10 w-10" />
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 pt-10">
            <h3 className="truncate text-lg font-semibold text-white">{event.name}</h3>
            <p className="text-xs text-white/80">
              {dateRange && <span>{dateRange}</span>}
              {dateRange && event.count != null && <span className="px-1.5">·</span>}
              {event.count != null && (
                <span>
                  {event.count} item{event.count !== 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>
        </div>
      </Link>

      <DownloadProgress
        phase={download.phase}
        done={download.done}
        total={download.total}
        onCancel={download.cancel}
      />
    </motion.div>
  );
}
